import { z } from 'zod';
/**
 * Wordle — speed + deduction.
 *
 * Standard rules: 6 rows, per-letter colour feedback. Both players get the same
 * seeded answer AND the same seeded word length.
 *
 * The word is **4 or 5 letters**, chosen per segment from the match seed. A
 * recipe containing two Wordles can therefore serve a 4-letter puzzle in one and
 * a 5-letter puzzle in the other — identically for both players, so fairness
 * holds. The client learns the length from its state-view; it is never assumed.
 *
 * Scoring: `elapsed + penalties`. On a SOLVE, each guess beyond the first costs
 * WORDLE_EXTRA_GUESS_PENALTY_MS, so the score reflects deduction rather than
 * typing speed. On a FAILURE, guesses are free and a flat
 * WORDLE_FAILED_SOLVE_PENALTY_MS applies instead — charging both would punish
 * trying harder than not trying (see plan doc §5.3).
 */
/** Puzzle lengths this game can produce. */
export const WORD_LENGTHS = [4, 5];
export const MIN_WORD_LENGTH = 4;
export const MAX_WORD_LENGTH = 5;
export const MAX_GUESSES = 6;
/**
 * Shown under the Wordle header, and as the reason a guess bounced.
 *
 * Guesses are permissive (every dictionary word of the right length), so a
 * rejection means the word genuinely isn't one, or you already tried it.
 */
export const LANGUAGE_NOTE = 'SPEED + DEDUCTION';
export const LANGUAGE_HINT = 'Not in word list — or already guessed';
/* -------------------------------------------------------------------------- */
/* Client -> server events                                                     */
/* -------------------------------------------------------------------------- */
/**
 * A guess.
 *
 * The schema bounds the length to the range this game can produce; the ENGINE
 * enforces the exact length for the segment in play, because only it knows which
 * word was seeded. Encoding `5` here would make a 4-letter puzzle unplayable, and
 * putting the exact length on the wire would let a client argue about it.
 */
export const WordleGuessEvent = z.object({
    type: z.literal('guess'),
    word: z
        .string()
        .trim()
        .toLowerCase()
        .min(MIN_WORD_LENGTH)
        .max(MAX_WORD_LENGTH)
        .regex(/^[a-z]+$/, 'Guess must be letters a-z'),
});
/**
 * Buy a hint: reveals one letter and its exact position, at a flat time penalty.
 *
 * The player does not choose which letter. Position is picked server-side from
 * a seed-derived order, so both players who buy a hint on the same puzzle get
 * the same letter — the match stays fair.
 */
export const WordleHintEvent = z.object({
    type: z.literal('hint'),
});
export const WordleEvent = z.discriminatedUnion('type', [WordleGuessEvent, WordleHintEvent]);
//# sourceMappingURL=wordle.js.map